import type {GridColDef} from "@mui/x-data-grid-premium";
import {Typography} from "@mui/material";
import {NO_VALUE} from "~/constants";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {TaxonomyChangeRow} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import {ApproverNoteField} from "./ApproverNoteField";
import {RevertChangesCell} from "./RevertChangesCell";

export type ApprovalReviewColumnsOptions = {
    /** The category in the approver's copy */
    nodeOf: (nodeId: number) => m_taxonomy.Tree | undefined;
    /** No edits once the request is no longer pending */
    readOnly: boolean;
    /** While a save is running */
    isSaving: boolean;
    canRevert: (nodeId: number, change: m_taxonomy.Change) => boolean;
    onRevert: (nodeId: number, change: m_taxonomy.Change) => void;
    onSaveNote: (nodeId: number, note: string) => void;
};

/**
 * The review columns after the change columns (design of the current approval page: Received notes ·
 * Approver notes, and a revert per change).
 */
export function getApprovalReviewColumns({
                                             nodeOf,
                                             readOnly,
                                             isSaving,
                                             canRevert,
                                             onRevert,
                                             onSaveNote,
                                         }: ApprovalReviewColumnsOptions): GridColDef<TaxonomyChangeRow>[] {
    const columns: GridColDef<TaxonomyChangeRow>[] = [
        {
            // The requester's note on the category, from the builder's approval preview.
            field: 'receivedNote',
            headerName: 'Received note',
            flex: 1,
            minWidth: 180,
            valueGetter: (_, row) => nodeOf(row.nodeId)?.values.approval_note ?? '',
            renderCell: ({value}) => (
                <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} sx={{py: 1, whiteSpace: 'normal'}}>
                    {value || NO_VALUE}
                </Typography>
            ),
        },
        {
            field: 'approverNote',
            headerName: 'Approver note',
            flex: 1,
            minWidth: 220,
            valueGetter: (_, row) => nodeOf(row.nodeId)?.values.approval_feedback_note ?? '',
            renderCell: ({row, value}) => readOnly
                ? (
                    <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} sx={{py: 1, whiteSpace: 'normal'}}>
                        {value || NO_VALUE}
                    </Typography>
                )
                : <ApproverNoteField note={value} disabled={isSaving} onSave={note => onSaveNote(row.nodeId, note)}/>,
        },
    ];
    if (readOnly) return columns;
    return [
        ...columns,
        {
            field: 'revert',
            headerName: 'Revert',
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: ({row}) => (
                <RevertChangesCell
                    changes={row.changes}
                    canRevert={change => canRevert(row.nodeId, change)}
                    onRevert={change => onRevert(row.nodeId, change)}
                    disabled={isSaving}
                />
            ),
        },
    ];
}
